import { useToast } from 'vue-toastification';
import type { ToastOptions } from 'vue-toastification/dist/types/types';
import StatusToast, { type ToastSeverity } from '~/components/toastify/StatusToast.vue';

type GlobalToastOptions = Omit<ToastOptions, 'type'>;

export const useGlobalToast = () => {
    const toast = useToast();

    const defaultToastOptions: GlobalToastOptions = {
        timeout: 3000,
        closeOnClick: true,
        pauseOnFocusLoss: true,
        pauseOnHover: false,
        draggable: false,
        draggablePercent: 0.6,
        showCloseButtonOnHover: false,
        hideProgressBar: true,
        closeButton: 'button',
        icon: false,
        rtl: false
    };

    const show = (
        method: 'success' | 'error' | 'warning' | 'info',
        severity: ToastSeverity,
        title: string,
        text: string,
        options?: GlobalToastOptions
    ) => {
        if (!import.meta.client) return;
        toast[method]({
            component: StatusToast,
            props: {
                severity,
                title,
                text
            }
        }, {
            ...defaultToastOptions,
            ...options
        });
    };

    const showSuccess = (title: string, text: string, options?: GlobalToastOptions) =>
        show('success', 'success', title, text, options);

    const showError = (title: string, text: string, options?: GlobalToastOptions) =>
        show('error', 'error', title, text, options);

    const showWarning = (title: string, text: string, options?: GlobalToastOptions) =>
        show('warning', 'warning', title, text, options);

    const showInfo = (title: string, text: string, options?: GlobalToastOptions) =>
        show('info', 'warning', title, text, options);

    const handleClearToast = () => {
        if (!import.meta.client) return;
        toast.clear();
    };

    return {
        showSuccess,
        showError,
        showWarning,
        showInfo,
        handleClearToast
    };
};
